/* Admin · Knowledge Base — the prototype's document inventory.
 *
 * KPI tiles, the filter bar, the lime-headed table, per-document Details / Access /
 * Re-index actions, CSV export, and the "ingestion is polling-only" note. Access is
 * read-only by design (derived from SharePoint via Graph) — the modal explains that and
 * offers the access probe instead of an editor.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'

import { admin } from '../../api/client'
import type {
  DocumentListResponse,
  DocumentStatus,
  DocumentSummary,
  ExtractedTextResponse,
} from '../../api/types'
import {
  Card,
  EmptyState,
  Kpi,
  PageHead,
  Skeleton,
  dateHuman,
  errorMessage,
  nf,
  useConfirm,
  useModal,
  useToast,
} from '../../ui/chrome'
import { Icon } from '../../ui/icons'
import { exportCSV } from '../../ui/pdf'

/* Categories are whatever the ingested corpus actually uses (locally: the data/kb folder
 * names; later: SharePoint library metadata) — so the filter list is learned from the
 * documents themselves rather than hardcoded. */

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'indexed', label: 'Live' },
  { value: 'archived', label: 'Superseded' },
  { value: 'pending_review', label: 'Awaiting review' },
  { value: 'extracting', label: 'Processing' },
  { value: 'discovered', label: 'Queued' },
  { value: 'failed', label: 'Failed' },
]

const PROCESSING: DocumentStatus[] = ['fetching', 'extracting', 'ocr', 'chunking']

/* The table pages at 25 rows. The fetch still pulls the whole filtered set in one call so
 * the KPI tiles and the CSV export keep covering every matching document, not just the
 * rows currently on screen — paging is a reading aid over that set, not a narrower query. */
const PAGE_SIZE = 25

/* Seed fixtures carry a "[SAMPLE]" prefix in their stored name. The marker is real
 * information — those documents contain fabricated policy text and the assistant will cite
 * them — so it is not thrown away, just moved out of the title into a badge. Editing the
 * stored name instead would hide from a reviewer that the row is not a real document. */
const SAMPLE_PREFIX = /^\s*\[sample\]\s*/i

export function isSampleDocument(name: string): boolean {
  return SAMPLE_PREFIX.test(name)
}

/** The document's name as a person would say it: no marker, no identifiers, no extension. */
export function displayName(name: string): string {
  return name.replace(SAMPLE_PREFIX, '').replace(/\.(pdf|docx?|pptx?|xlsx?|txt|md)$/i, '').trim()
}

/* ------------------------------------------------------------------ extraction review */

/**
 * Side-by-side validation of what the assistant actually reads.
 *
 * The document list can say a file is `indexed` with 370 pages and 500 chunks and be telling
 * the truth while the text behind it is unusable — a scanned table transcribed as noise, a
 * page that returned nothing but a running header. Nothing downstream can catch that: an
 * answer citing a badly-extracted page is indistinguishable, to every automated check we
 * have, from an answer citing a good one. Only a person comparing the two can tell, so this
 * puts the original page and the stored text next to each other and gets out of the way.
 *
 * The extracted text is rendered verbatim in a <pre> — not trimmed, not collapsed, not
 * prettified. Whitespace and line breaks are exactly what the chunker saw, and hiding them
 * would hide the most common extraction faults.
 */
function ExtractionReview({ detail }: { detail: ExtractedTextResponse }) {
  const [selected, setSelected] = useState<number>(detail.pages[0]?.page_number ?? 1)
  const page = detail.pages.find((p) => p.page_number === selected) ?? detail.pages[0]
  // Pre-signed by the server. Building this path by hand produces a 401: the browser fetches
  // it directly and cannot attach the auth header our fetch() wrapper normally adds.
  const sourceUrl = detail.source_url

  if (!page) {
    return (
      <EmptyState
        icon="alert"
        title="No extracted text"
        msg="This document has no stored page text."
      />
    )
  }

  return (
    <div>
      <div className="stat-line">
        <span className="sl-k">Pages</span>
        <span className="sl-v">
          {detail.extracted_page_count} extracted
          {detail.page_count > 0 && ` of ${detail.page_count} in the file`}
          {' · '}
          {nf(detail.total_chars)} characters
          {detail.ocr_applied && ' · OCR applied'}
        </span>
      </div>

      {detail.missing_page_numbers.length > 0 && (
        <div className="alert a-danger mt12">
          <Icon name="alert" size={18} />
          <div>
            <strong>{detail.missing_page_numbers.length} page(s) produced no text</strong>
            The assistant cannot see these pages at all: {detail.missing_page_numbers.slice(0, 20).join(', ')}
            {detail.missing_page_numbers.length > 20 && ' …'}
          </div>
        </div>
      )}
      {detail.thin_page_numbers.length > 0 && (
        <div className="alert a-warn mt12">
          <Icon name="alert" size={18} />
          <div>
            <strong>{detail.thin_page_numbers.length} page(s) returned very little text</strong>
            Often legitimate — cover pages and dividers — but this is where extraction failures
            show up. Worth checking: {detail.thin_page_numbers.slice(0, 20).join(', ')}
            {detail.thin_page_numbers.length > 20 && ' …'}
          </div>
        </div>
      )}

      {/* Page picker. Thin pages are marked so a reviewer can go straight to the suspicious
          ones rather than paging through a 370-page report looking for them. */}
      <div className="mt12" style={{ maxHeight: 90, overflowY: 'auto' }}>
        <div className="cite-row" style={{ flexWrap: 'wrap', gap: 4 }}>
          {detail.pages.map((p) => (
            <button
              key={p.page_number}
              type="button"
              className={`cite-pill${p.page_number === selected ? ' on' : ''}`}
              title={
                p.looks_thin
                  ? `Page ${p.page_number} — only ${p.char_count} characters extracted`
                  : `Page ${p.page_number} — ${p.char_count} characters`
              }
              onClick={() => setSelected(p.page_number)}
              style={p.looks_thin ? { borderColor: 'var(--warn, #b8860b)' } : undefined}
            >
              {p.page_number}
              {p.looks_thin && ' ⚠'}
            </button>
          ))}
        </div>
      </div>

      <div className="grid g2 mt12">
        <div className="card" style={{ marginBottom: 0 }}>
          <div className="card-h">
            <h3>Original document — page {page.page_number}</h3>
          </div>
          <div className="card-b" style={{ padding: 0 }}>
            {detail.source_available && sourceUrl ? (
              <iframe
                title={`Source page ${page.page_number}`}
                /* key forces a reload when the page changes — a #fragment change alone does
                   not re-navigate an already-loaded PDF viewer. */
                key={page.page_number}
                src={`${sourceUrl}#page=${page.page_number}`}
                style={{ width: '100%', height: 460, border: 0 }}
              />
            ) : (
              <div className="tiny muted" style={{ padding: 16 }}>
                The original file is not stored on this server.
              </div>
            )}
          </div>
        </div>

        <div className="card" style={{ marginBottom: 0 }}>
          <div className="card-h">
            <h3>Extracted text — page {page.page_number}</h3>
            <span className="tiny muted">
              {nf(page.char_count)} chars · {page.chunk_count} chunk
              {page.chunk_count === 1 ? '' : 's'}
              {page.ocr_applied ? ' · OCR' : ''}
              {page.ocr_confidence != null && ` · confidence ${page.ocr_confidence}`}
            </span>
          </div>
          <div className="card-b">
            {page.chunk_count === 0 && (
              <div className="alert a-warn" style={{ marginBottom: 8 }}>
                <Icon name="alert" size={18} />
                <div>
                  <strong>Not retrievable</strong>
                  Text was extracted from this page but no chunk covers it.
                </div>
              </div>
            )}
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: 420,
                overflowY: 'auto',
                margin: 0,
                fontSize: 12,
                lineHeight: 1.5,
              }}
            >
              {page.content || '(no text extracted from this page)'}
            </pre>
          </div>
        </div>
      </div>
    </div>
  )
}

export function statusPill(d: DocumentSummary) {
  switch (d.status) {
    case 'indexed':
      return <span className="pill p-ok">Live</span>
    case 'archived':
      return <span className="pill p-grey">Superseded</span>
    case 'pending_review':
      return <span className="pill p-warn">Awaiting review</span>
    case 'failed':
      return <span className="pill p-danger">Failed</span>
    case 'discovered':
      return <span className="pill p-grey">Queued</span>
    default:
      return (
        <span className="pill p-info">
          {d.stage_detail ?? d.status} {d.progress_pct}%
        </span>
      )
  }
}

export default function DocumentsScreen() {
  const toast = useToast()
  const confirm = useConfirm()
  const { openModal, closeModal } = useModal()

  const [data, setData] = useState<DocumentListResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [q, setQ] = useState('')
  const [cat, setCat] = useState('')
  const [status, setStatus] = useState('')
  // Every category ever seen this session, so the dropdown doesn't shrink to the current
  // filter's own result set.
  const [knownCats, setKnownCats] = useState<string[]>([])
  const [page, setPage] = useState(0)

  const refresh = useCallback(() => {
    admin
      .documents({ q: q || undefined, category: cat || undefined, status: status || undefined, limit: 200 })
      .then((response) => {
        setData(response)
        setError(null)
        setKnownCats((known) => {
          const seen = new Set(known)
          for (const d of response.items) if (d.category) seen.add(d.category)
          return Array.from(seen).sort()
        })
      })
      .catch((cause: unknown) => setError(errorMessage(cause)))
  }, [q, cat, status])

  useEffect(() => {
    const timer = setTimeout(refresh, q ? 250 : 0)
    return () => clearTimeout(timer)
  }, [refresh, q])

  // A narrower filter can leave the reader stranded past the last page.
  useEffect(() => setPage(0), [q, cat, status])

  const docs = useMemo(() => data?.items ?? [], [data])
  // From the server, over the whole filtered set. Deriving it from `docs` counted the fetched
  // window only, which is a different question than the one the tile asks.
  const liveCount = data?.live_count ?? 0
  const chunkTotal = data?.chunk_count ?? 0
  const hilCount = data?.awaiting_review ?? 0

  const pageCount = Math.max(1, Math.ceil(docs.length / PAGE_SIZE))
  const safePage = Math.min(page, pageCount - 1)
  const pageStart = safePage * PAGE_SIZE
  const pageDocs = docs.slice(pageStart, pageStart + PAGE_SIZE)

  function doExport() {
    exportCSV('kbl-document-inventory.csv', [
      [
        'Document ID',
        'Name',
        'Category',
        'Source',
        'Library',
        'Status',
        'OCR applied',
        'Pages',
        'Chunks',
        'Access groups',
        'Last updated',
      ],
      ...docs.map((d) => [
        d.id,
        d.name,
        d.category ?? '',
        d.source,
        d.source_label ?? '',
        d.status,
        d.ocr_applied ? 'yes' : 'no',
        d.page_count ?? '',
        d.chunk_count,
        d.access_groups.join(' | '),
        d.indexed_at ?? d.source_modified_at ?? '',
      ]),
    ])
    toast('ok', 'Export complete', `kbl-document-inventory.csv downloaded (${docs.length} rows).`)
  }

  function openExtracted(d: DocumentSummary) {
    admin
      .extractedText(d.id)
      .then((detail) => {
        openModal({
          title: `Extracted data — ${displayName(d.name)}`,
          size: 'wide',
          body: <ExtractionReview detail={detail} />,
          footer: (
            <>
              <button type="button" className="btn btn-soft" onClick={closeModal}>
                Close
              </button>
              {detail.source_url && (
                <a
                  className="btn btn-ghost"
                  href={detail.source_url}
                  target="_blank"
                  rel="noreferrer"
                  style={{ textDecoration: 'none' }}
                >
                  <Icon name="ext" size={16} /> Open original
                </a>
              )}
              <button
                type="button"
                className="btn"
                onClick={() => {
                  closeModal()
                  reindex(d)
                }}
              >
                <Icon name="refresh" size={16} /> Re-extract
              </button>
            </>
          ),
        })
      })
      .catch((error) => toast('err', 'Could not load extracted text', errorMessage(error)))
  }

  function openDetails(d: DocumentSummary) {
    openModal({
      title: displayName(d.name),
      sub: `${d.category ?? 'Uncategorised'} · ${d.page_count ?? '—'} pages · ${nf(
        d.chunk_count,
      )} indexed chunks`,
      size: 'wide',
      body: (
        <div className="grid g2">
          <div className="card" style={{ marginBottom: 0 }}>
            <div className="card-h">
              <h3>Metadata</h3>
            </div>
            <div className="card-b">
              {[
                ['Status', statusPill(d)],
                ['Source', `${d.source}${d.source_label ? ` / ${d.source_label}` : ''}`],
                ['Pages', d.page_count ?? '—'],
                ['Indexed chunks', nf(d.chunk_count)],
                ['OCR applied', d.ocr_applied ? 'Yes' : 'No'],
                ['Uploaded by', d.uploaded_by ?? '—'],
                ['Source modified', dateHuman(d.source_modified_at)],
                ['Last polled', dateHuman(d.last_polled_at)],
                ['Indexed', dateHuman(d.indexed_at)],
                // Kept here rather than in the list: needed when raising a ticket, never
                // when reading the table.
                ['Document ID', <span className="tiny mono">{d.id}</span>],
              ].map(([k, v]) => (
                <div key={String(k)} className="stat-line">
                  <span className="sl-k">{k}</span>
                  <span className="sl-v">{v}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="card" style={{ marginBottom: 0 }}>
            <div className="card-h">
              <h3>Usage &amp; access</h3>
            </div>
            <div className="card-b">
              <div className="stat-line">
                <span className="sl-k">Access groups</span>
                <span className="sl-v">
                  {d.access_groups.length > 0
                    ? d.access_groups.map((g) => (
                        <span key={g} className="tag grp">
                          {g}
                        </span>
                      ))
                    : '—'}
                </span>
              </div>
              <div className="stat-line">
                <span className="sl-k">Retry count</span>
                <span className="sl-v">{d.retry_count}</span>
              </div>
              {d.web_url && (
                <div className="stat-line">
                  <span className="sl-k">Source link</span>
                  <span className="sl-v">
                    <a href={d.web_url} target="_blank" rel="noreferrer">
                      Open in {d.source === 'sharepoint' ? 'SharePoint' : 'OneDrive'}
                    </a>
                  </span>
                </div>
              )}
              {d.error_message && (
                <div className="alert a-danger mt12">
                  <Icon name="alert" size={18} />
                  <div>
                    <strong>Ingestion failed</strong>
                    {d.error_message}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ),
      footer: (
        <>
          <button type="button" className="btn btn-soft" onClick={closeModal}>
            Close
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => {
              closeModal()
              reindex(d)
            }}
          >
            <Icon name="refresh" size={16} /> Re-index
          </button>
        </>
      ),
    })
  }

  function openAccess(d: DocumentSummary) {
    admin
      .documentAccess(d.id)
      .then((detail) => {
        let probeUpn = ''
        openModal({
          title: `Access groups — ${d.name}`,
          body: (
            <div>
              {detail.permissions_inherited && (
                <div className="alert a-info">
                  <Icon name="info" size={18} />
                  <div>Permissions are inherited from the parent library rather than set per file.</div>
                </div>
              )}
              {detail.groups.map((g) => (
                <div key={`${g.display_name}-${g.entra_group_id ?? 'x'}`} className="stat-line">
                  <span className="sl-k">{g.display_name}</span>
                  <span className="sl-v">
                    {g.mapped ? (
                      <>
                        <span className="tag grp">{g.entra_group_id}</span>{' '}
                        <span className="tiny muted">
                          {g.sharepoint_grant}
                          {g.member_count !== null && ` · ${nf(g.member_count)} members`}
                        </span>
                      </>
                    ) : (
                      <span className="pill p-warn">Unmapped</span>
                    )}
                  </span>
                </div>
              ))}
              <div className="stat-line">
                <span className="sl-k">Last reconciled</span>
                <span className="sl-v">{dateHuman(detail.last_reconciled_at)}</span>
              </div>

              <div className="field mt16">
                <label htmlFor="probeUpn">Can this person retrieve it?</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    id="probeUpn"
                    className="inp"
                    placeholder="name@kbl.co.in"
                    onChange={(event) => {
                      probeUpn = event.target.value
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn-lime"
                    onClick={() => {
                      const upn = probeUpn.trim()
                      if (!upn) {
                        toast('warn', 'Enter a corporate email to check')
                        return
                      }
                      admin
                        .probeAccess(d.id, upn)
                        .then((result) => {
                          toast(
                            result.can_retrieve ? 'ok' : 'warn',
                            result.can_retrieve
                              ? `${result.display_name ?? upn} can retrieve this document`
                              : `${result.display_name ?? upn} cannot retrieve this document`,
                            result.explanation,
                            7000,
                          )
                        })
                        .catch((cause: unknown) => toast('err', 'Probe failed', errorMessage(cause)))
                    }}
                  >
                    Check
                  </button>
                </div>
              </div>
            </div>
          ),
        })
      })
      .catch((cause: unknown) => toast('err', 'Access detail unavailable', errorMessage(cause)))
  }

  function reindex(d: DocumentSummary) {
    confirm(
      'Re-index this document?',
      `“${d.name}” will be re-fetched, re-extracted and re-chunked, then queued for reviewer approval before returning to the live index.`,
      'Re-index',
      () => {
        admin
          .reindex(d.id)
          .then(() => {
            toast('info', 'Re-index started', d.name)
            refresh()
          })
          .catch((cause: unknown) => toast('err', 'Re-index failed', errorMessage(cause)))
      },
    )
  }

  return (
    <>
      <PageHead
        title="Knowledge Base"
        actions={
          <button type="button" className="btn btn-soft" onClick={doExport} disabled={docs.length === 0}>
            <Icon name="dl" size={16} /> Export CSV
          </button>
        }
      />

      <div className="grid g4 mb16">
        <Kpi
          label="Total documents"
          value={data ? nf(data.total) : '—'}
          hoverDesc={data ? `Up to ${nf(data.cap_status.cap)} documents can be added` : undefined}
        />
        <Kpi tone="green" label="Live & searchable" value={nf(liveCount)} hoverDesc="Checked and used in answers" />
        <Kpi tone="info" label="Indexed passages" value={nf(chunkTotal)} hoverDesc="Text sections ready to search" />
        <Kpi tone="warn" label="Awaiting review" value={nf(hilCount)} hoverDesc="Not used in answers until reviewed" />
      </div>

      <div className="filters">
        <div className="field">
          <label htmlFor="dfQ">Search</label>
          <input
            id="dfQ"
            className="inp"
            placeholder="Document name"
            value={q}
            onChange={(event) => setQ(event.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="dfCat">Category</label>
          <select id="dfCat" className="inp" value={cat} onChange={(event) => setCat(event.target.value)}>
            <option value="">All</option>
            {knownCats.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="dfStatus">Status</label>
          <select
            id="dfStatus"
            className="inp"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            <option value="">All</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          className="btn btn-soft"
          onClick={() => {
            setQ('')
            setCat('')
            setStatus('')
            toast('info', 'Filters cleared')
          }}
        >
          <Icon name="refresh" size={16} /> Reset
        </button>
      </div>

      {error && (
        <div className="alert a-danger">
          <Icon name="alert" size={18} />
          <div>
            <strong>The document list could not be loaded.</strong>
            {error}
          </div>
        </div>
      )}

      <Card icon="book" title="Documents" hint={data ? `${docs.length} of ${nf(data.total)}` : undefined} tight>
        {!data && !error ? (
          <Skeleton rows={6} />
        ) : docs.length === 0 ? (
          <EmptyState
            icon="doc"
            title="No documents match"
            msg="Adjust or clear the filters."
            action={
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => {
                  setQ('')
                  setCat('')
                  setStatus('')
                }}
              >
                Clear filters
              </button>
            }
          />
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Document</th>
                  <th>Category</th>
                  <th>Source</th>
                  <th>Status</th>
                  <th className="num">Pages</th>
                  <th className="num">Chunks</th>
                  <th>Updated</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageDocs.map((d) => (
                  <tr key={d.id}>
                    {/* Name only. The UUID and the source label were the two things a
                        reviewer never needs while scanning the list and could not avoid
                        reading; both are still available, in Details, where they are
                        actually used. */}
                    <td>
                      <strong>{displayName(d.name)}</strong>
                      {isSampleDocument(d.name) && (
                        <>
                          {' '}
                          <span
                            className="pill p-grey"
                            title="Seed fixture — this document contains sample text, not a real KBL policy."
                          >
                            sample
                          </span>
                        </>
                      )}
                    </td>
                    <td>
                      <span className="pill p-grey">{d.category ?? '—'}</span>
                    </td>
                    <td>
                      <span className="tiny">
                        {d.source === 'sharepoint' ? 'SharePoint' : 'OneDrive'}
                      </span>
                      {d.ocr_applied && (
                        <>
                          <br />
                          <span className="tiny muted">OCR</span>
                        </>
                      )}
                    </td>
                    <td>
                      {statusPill(d)}
                      {PROCESSING.includes(d.status) && (
                        <div className="progress mt8" style={{ width: 120 }}>
                          <i style={{ width: `${d.progress_pct}%` }} />
                        </div>
                      )}
                    </td>
                    <td className="num">{d.page_count ?? '—'}</td>
                    <td className="num">{nf(d.chunk_count)}</td>
                    <td>
                      <span className="tiny nowrap">
                        {dateHuman(d.indexed_at ?? d.source_modified_at)}
                      </span>
                    </td>
                    <td>
                      <div className="actions">
                        <button
                          type="button"
                          className="btn-icon"
                          title="Details"
                          onClick={() => openDetails(d)}
                        >
                          <Icon name="info" size={15} />
                        </button>
                        <button
                          type="button"
                          className="btn-icon"
                          title="Extracted data — check the stored text against the original"
                          onClick={() => openExtracted(d)}
                        >
                          <Icon name="book" size={15} />
                        </button>
                        <button
                          type="button"
                          className="btn-icon"
                          title="Access groups"
                          onClick={() => openAccess(d)}
                        >
                          <Icon name="shield" size={15} />
                        </button>
                        <button
                          type="button"
                          className="btn-icon"
                          title="Re-index"
                          onClick={() => reindex(d)}
                        >
                          <Icon name="refresh" size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {docs.length > 0 && (
          <div className="tbl-pager">
            <span className="tp-count">
              {nf(pageStart + 1)}–{nf(pageStart + pageDocs.length)} of {nf(docs.length)}
            </span>
            <div className="tp-nav">
              <button
                type="button"
                className="btn btn-sm btn-soft"
                disabled={safePage === 0}
                onClick={() => setPage(safePage - 1)}
              >
                <Icon name="chevL" size={14} /> Previous
              </button>
              <span className="tp-pos">
                Page {safePage + 1} of {pageCount}
              </span>
              <button
                type="button"
                className="btn btn-sm btn-soft"
                disabled={safePage >= pageCount - 1}
                onClick={() => setPage(safePage + 1)}
              >
                Next <Icon name="chevR" size={14} />
              </button>
            </div>
          </div>
        )}
      </Card>
    </>
  )
}
